import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { ensurePoCustomerLink } from "@/lib/ensure-po-customer";
import { logEvent } from "@/hooks/use-event-log";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { normalizeCountryCode } from "@/lib/countries";

/** Hvor kunden ble opprettet. Utelatt fra prosjekt- og tilbudsflyten. */
export type CustomerCreateSource = "kunder";

export interface CreateCustomerInput {
  name: string;
  organization_number?: string | null;
  contact_person?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  /** ISO 3166-1 alpha-2. Default NO. */
  country_code?: string | null;
  source?: CustomerCreateSource;
}

interface CreateCustomerResult {
  id: string;
}

/**
 * Oppretter en ny kunde via direkte insert i `customers`-tabellen.
 * Brukes av `NewCustomerDialog` i ny-prosjekt- og tilbuds-flyten. Invaliderer
 * `["customers"]` så `useCustomers` plukker opp den nye raden uten
 * å vente på en full refresh.
 *
 * `toast.error` kalles ved feil for konsistent UX med andre mutations.
 * Caller kan fortsatt observere `isError` for å holde dialog åpen.
 *
 * Etter insert pushes kunden til PowerOffice via `ensure-po-customer`
 * (best-effort). Uten PO-kobling feiler `create-invoice` senere, så vi
 * varsler tydelig hvis pushen ikke gikk gjennom.
 */
export function useCreateCustomer() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const { activeUser, isImpersonating } = useImpersonation();
  return useMutation<CreateCustomerResult, Error, CreateCustomerInput>({
    mutationFn: async (input) => {
      const payload = {
        name: input.name.trim(),
        organization_number: input.organization_number?.trim() || null,
        contact_person: input.contact_person ?? null,
        contact_email: input.contact_email ?? null,
        contact_phone: input.contact_phone ?? null,
        address: input.address ?? null,
        postal_code: input.postal_code ?? null,
        city: input.city ?? null,
        country_code: normalizeCountryCode(input.country_code),
      };
      const { data, error } = await supabase
        .from("customers")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      if (!data?.id) throw new Error("Manglet id i opprett-respons");
      return { id: data.id };
    },
    onSuccess: async (data, input) => {
      if (authUser?.id) {
        logEvent({
          event_type: "customer_created",
          entity_type: "customer",
          entity_id: data.id,
          user_id: authUser.id,
          payload: {
            name: input.name.trim(),
            organization_number: input.organization_number?.trim() || null,
            country_code: normalizeCountryCode(input.country_code),
            ...(input.source ? { source: input.source } : {}),
            ...(isImpersonating && activeUser ? { on_behalf_of: activeUser.id } : {}),
          },
        });
      }
      const link = await ensurePoCustomerLink(data.id);
      if (link.method === "error") {
        // Grunnen MÅ med: toasten er det eneste PL-en ser der og da. Feilen
        // logges som customer_po_link_failed i ensure-po-customer, med
        // aktøren fra JWT-en (VIB-2575), og kundekortet viser «Ikke koblet
        // til PowerOffice» med en knapp for nytt forsøk.
        toast.warning(
          "Kunde opprettet, men ikke koblet til PowerOffice. Fakturering vil ikke fungere før dette er fikset.",
          { description: link.error },
        );
      }
      await invalidateFor(qc, "customers", { customerId: data.id });
    },
    onError: (err) => {
      toast.error(`Feil: ${err.message}`);
    },
  });
}
